import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", type: "GOOD" };

const Warehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadWarehouses = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/warehouses", { size: 200 });
      setWarehouses(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load warehouses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWarehouses();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (warehouse) => {
    setEditingId(warehouse.id);
    setFormData({ name: warehouse.name || "", type: warehouse.type || "GOOD" });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/warehouses/${editingId}`, formData);
        toast.success("Warehouse updated");
      } else {
        await api.post("/api/v1/warehouses", formData);
        toast.success("Warehouse created");
      }
      setShowModal(false);
      loadWarehouses();
    } catch (error) {
      toast.error(error.message || "Failed to save warehouse");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading warehouses...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Good stock warehouses hold sellable inventory. Bad stock warehouses hold damaged/rejected goods and
        customer returns pending QC — returned goods land here automatically, never straight back into sellable
        stock.
      </div>

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Warehouse
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Type</th>
              <th>Default</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {warehouses.length === 0 ? (
              <tr>
                <td colSpan={5}>No warehouses found.</td>
              </tr>
            ) : (
              warehouses.map((warehouse) => (
                <tr key={warehouse.id}>
                  <td>{warehouse.id}</td>
                  <td>{warehouse.name}</td>
                  <td>
                    <span className={`badge ${warehouse.type === "BAD" ? "badge-red" : "badge-green"}`}>
                      {warehouse.type === "BAD" ? "Bad Stock" : "Good Stock"}
                    </span>
                  </td>
                  <td>{warehouse.defaultWarehouse ? "Yes" : "-"}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(warehouse)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Warehouse" : "Create Warehouse"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Main Godown" />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select name="type" value={formData.type} onChange={handleChange}>
                    <option value="GOOD">Good Stock (sellable)</option>
                    <option value="BAD">Bad Stock (damaged / returns / QC)</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Warehouses;
